import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getSlottingFlags } from "@/lib/warehouse-map.functions";
import { useScope } from "@/hooks/use-scope";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/app/warehouse-map/slotting")({
  component: SlottingTab,
});

type Flag = {
  sku_id: string; sku_code: string; sku_name: string;
  location_id: string; location_code: string;
  reason: string; severity: "high" | "medium" | "low"; pick_count: number;
};

function SlottingTab() {
  const { tenantId, facilityId } = useScope();
  const getFlags = useServerFn(getSlottingFlags);
  const [days, setDays] = useState(30);

  const { data, isLoading } = useQuery({
    queryKey: ["wm-slot", tenantId, facilityId, days],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getFlags({ data: { tenantId: tenantId!, facilityId: facilityId!, days } }),
  });

  const columns: ColumnDef<Flag, any>[] = [
    { accessorKey: "severity", header: "Severity", cell: ({ row }) => {
      const s = row.original.severity;
      const v = s === "high" ? "destructive" : s === "medium" ? "default" : "secondary";
      return <Badge variant={v as any} className="capitalize">{s}</Badge>;
    }},
    { accessorKey: "sku_code", header: "SKU", cell: ({ row }) => (
      <div className="text-xs">
        <div className="font-mono">{row.original.sku_code}</div>
        <div className="text-muted-foreground truncate max-w-[220px]">{row.original.sku_name}</div>
      </div>
    )},
    { accessorKey: "location_code", header: "Location", cell: ({ row }) => (
      <span className="font-mono text-xs">{row.original.location_code}</span>
    )},
    { accessorKey: "reason", header: "Reason" },
    { accessorKey: "pick_count", header: "Picks", cell: ({ row }) => (
      <span className="font-mono tabular-nums">{row.original.pick_count}</span>
    )},
  ];

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {data?.flags.length ?? 0} flagged placements
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)}>{d}d</Button>
          ))}
        </div>
      </div>
      <DataTable
        data={data?.flags ?? []}
        columns={columns}
        isLoading={isLoading}
        empty={{ title: "No slotting flags", description: "SKU placement looks healthy for this window." }}
      />
    </div>
  );
}